import { BaseModel } from '@/api/base-model'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'
import { Contact } from '@/api/model/contact'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { StatusReason } from '@/api/model/status-reason'
import { ReferralAPI } from '@/api/backend-client'
import { Attachment } from '@/api/model/attachment'
import { Comment } from '@/api/model/comment'
import { ReferralStatus, ReferralStatusReason, ReferralType } from '@/api/types'
import { Project } from '@/api/model/project'
import { EventLog } from '@/api/model/event-log'
import { Action } from '@/api/model/action'
import moment from 'moment'
import { Source } from '@/api/model/source'

interface FlattenedReferralPermissions {
    branches: string[],
    users: string[],
}

export class Referral extends BaseModel<Referral> {
    protected collectionName = ReferralAPI.collectionName

    constructor(
        public contact: Contact,
        public id: string,
        public type: string,
        public pipeline: Pipeline|null,
        public phase: PipelinePhase|null,
        public referredBy: User|null,
        public referredByBranch: Branch|null,
        public assignedTo: User|null,
        public assignedToBranch: Branch|null,
        public priority: boolean,
        public status: ReferralStatus,
        public statusReason: ReferralStatusReason|null,
        public newStatusReason: StatusReason|null,
        public nextActionAt: Date|null,
        public nextOrPreviousAction: Action|null,
        public nextOrPreviousAppointment: Action|null,
        public medianValuation: number|null,
        public manualValuation: number|null,
        public appointment: Date|null,
        public appointmentLocation: string|null,
        public newBuildType: string|null,
        public futureStreetname: string|null,
        public futureStreetnumber: string|null,
        public futureStreetnumbersuffix: string|null,
        public futureZipcode: string|null,
        public futureCity: string|null,
        public futureCountry: string|null,
        public futureLatitude: string|null,
        public futureLongitude: string|null,
        public futureFrom: Date|null,
        public project: Project|null,
        public assigneeProject: Project|null,
        public buildNumber: string|null,
        public assigneeBuildNumber: string|null,
        public orderValue: number|null,
        public leadCommission: number|null,
        public nettoOrderValue: number|null,
        public channel: string,
        public source: string,
        public newSource: Source|null,
        public transportDate: Date|null,
        public productionDate: Date|null,
        public score: number,
        public data: {[key: string]: any},
        public attachments: Attachment[],
        public comments: Comment[],
        public actions: Action[],
        public eventLogs: EventLog[],
        public permissions: FlattenedReferralPermissions|null,
        public createdAt: Date|null,
        public updatedAt: Date|null,
        public successfulAt: Date|null,
        public unsuccessfulAt: Date|null,
    ) {
        super(id)
    }

    get hasNextActionData(): boolean {
        return this.nextActionAt !== null
    }

    get hasFutureAddressData(): boolean {
        return this.futureStreetname !== null
            || this.futureStreetnumber !== null
            || this.futureStreetnumbersuffix !== null
            || this.futureZipcode !== null
            || this.futureCity !== null
            || this.futureCountry !== null
    }

    get newSourceName(): string|null {
        return this.newSource?.name
    }

    get hasEmmaSource(): boolean {
        return this.newSource ? this.newSource.isEmmaSource : false
    }

    get hasOrderValue(): boolean {
        return this.orderValue !== null && this.orderValue > 0
    }

    get hasLeadCommission(): boolean {
        return this.leadCommission !== null && this.leadCommission > 0
    }

    get hasNettoOrderValue(): boolean {
        return this.nettoOrderValue !== null && this.nettoOrderValue > 0
    }

    get hasFinancialData(): boolean {
        return this.hasOrderValue
            || this.hasLeadCommission
            || this.hasNettoOrderValue
    }

    get hasAttachments(): boolean {
        return this.attachments.length > 0
    }

    get isEmmaAppraisal(): boolean {
        if ('valuation' in this.data) {
            return true
        }

        const emmaAppraisalTypes: ReferralType[] = [ReferralType.APPRAISAL, ReferralType.SALE]
        if (emmaAppraisalTypes.includes(this.type as ReferralType)) {
            return !!this.contact.zipcode && !!this.contact.streetnumber
        }

        return false
    }

    get inactiveSince(): Date {
        const inactiveSinceDates = [
            new Date(this.updatedAt),
            ...this.actions.map(action => new Date(action.actionDate)),
            ...this.comments.map(comment => new Date(comment.updatedAt)),
        ]

        const inactiveSince = moment(Math.max.apply(null, inactiveSinceDates)).add(1, 'day')

        return inactiveSince.toDate()
    }

    get autoCloseDate(): Date|null {
        const autoCloseSettings = this.assignedToBranch.company.autoCloseSettings
        if (!autoCloseSettings || !autoCloseSettings.closeEnabled) {
            return null
        }

        let closeAfterDays: number

        if (autoCloseSettings.perPipelineEnabled) {
            if (this.pipeline.autoCloseAfterDays === null) {
                return
            }

            closeAfterDays = this.pipeline.autoCloseAfterDays
        } else {
            closeAfterDays = autoCloseSettings.closeAfterDays
        }

        const autoCloseDate = moment(this.inactiveSince).add(closeAfterDays, 'days')
        const firstAutoCloseDate = moment(autoCloseSettings.closeEnabledAt).add(1 + autoCloseSettings.notificationBeforeDays, 'days')

        return autoCloseDate.isBefore(firstAutoCloseDate, 'day') ? firstAutoCloseDate.toDate() : autoCloseDate.toDate()
    }

    get autoCloseNotificationDate(): Date|null {
        const autoCloseSettings = this.assignedToBranch.company.autoCloseSettings
        if (!autoCloseSettings || !autoCloseSettings.closeEnabled || !autoCloseSettings.notificationEnabled) {
            return null
        }

        const autoCloseNotificationDate = moment(this.autoCloseDate).subtract(autoCloseSettings.notificationBeforeDays, 'days')

        return autoCloseNotificationDate.toDate()
    }

    static fromData(data: any): Referral {
        if (data === null || typeof data === 'string' || data instanceof Referral) {
            return data
        }

        return new Referral(
            data.contact ? Contact.fromData(data.contact) : null,
            data.id,
            data.type,
            data.pipeline ? Pipeline.fromData(data.pipeline) : null,
            data.phase ? PipelinePhase.fromData(data.phase) : null,
            data.referredBy ? User.fromData(data.referredBy) : null,
            data.referredByBranch ? Branch.fromData(data.referredByBranch) : null,
            data.assignedTo ? User.fromData(data.assignedTo) : null,
            data.assignedToBranch ? Branch.fromData(data.assignedToBranch) : null,
            data.priority || false,
            data.status,
            data.statusReason,
            data.newStatusReason ? StatusReason.fromData(data.newStatusReason) : null,
            data.nextActionAt,
            data.nextOrPreviousAction ? Action.fromData(data.nextOrPreviousAction) : null,
            data.nextOrPreviousAppointment ? Action.fromData(data.nextOrPreviousAppointment) : null,
            data.medianValuation,
            data.manualValuation,
            data.appointment,
            data.appointmentLocation,
            data.newBuildType,
            data.futureStreetname,
            data.futureStreetnumber,
            data.futureStreetnumbersuffix,
            data.futureZipcode,
            data.futureCity,
            data.futureCountry,
            data.futureLatitude,
            data.futureLongitude,
            data.futureFrom,
            data.project ? Project.fromData(data.project) : null,
            data.assigneeProject ? Project.fromData(data.assigneeProject) : null,
            data.buildNumber,
            data.assigneeBuildNumber,
            data.orderValue,
            data.leadCommission,
            data.nettoOrderValue,
            data.channel,
            data.source,
            data.newSource ? Source.fromData(data.newSource) : null,
            data.transportDate,
            data.productionDate,
            data.score,
            data.data,
            Object.values(data.attachments || {}).map(entry => Attachment.fromData(entry)),
            Object.values(data.comments || {}).map(entry => Comment.fromData(entry)),
            Object.values(data.actions || {}).map(entry => Action.fromData(entry)),
            Object.values(data.eventLogs || {}).map(entry => EventLog.fromData(entry)),
            data.permissions,
            data.createdAt,
            data.updatedAt,
            data.successfulAt,
            data.unsuccessfulAt,
        )
    }
}
